import React, { useEffect, useMemo, useRef } from 'react';
import {
  EditorContent,
  useEditor
} from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { ResourceEditorMode } from '../constants/resourceEditor.constants';

interface ResourceRichTextEditorProps {
  mode: ResourceEditorMode;
  value: string;
  onChange: (nextValue: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
}

export const ResourceRichTextEditor: React.FC<
  ResourceRichTextEditorProps
> = ({
  mode,
  value,
  onChange,
  disabled = false,
  readOnly = false
}) => {
  const isInteractive =
    !disabled && !readOnly;
  const isSyncingFromValueRef = useRef(false);

  const normalizeOutgoingHtml = (
    html: string,
    editorEmpty: boolean
  ): string => {
    if (editorEmpty) {
      return '';
    }

    return html.trim();
  };

  const normalizedInitialContent = useMemo(() => {
    const trimmed =
      typeof value === 'string'
        ? value.trim()
        : '';

    return trimmed || '<p></p>';
  }, [value]);

  const editor = useEditor({
    extensions: [StarterKit],
    content: normalizedInitialContent,
    editable: isInteractive,
    onUpdate: ({ editor: nextEditor }) => {
      if (!isInteractive) {
        return;
      }

      if (isSyncingFromValueRef.current) {
        return;
      }

      const nextHtml = normalizeOutgoingHtml(
        nextEditor.getHTML(),
        nextEditor.isEmpty
      );

      onChange(nextHtml);
    }
  });

  useEffect(() => {
    if (!editor) {
      return;
    }

    const normalizedIncoming =
      typeof value === 'string' && value.trim()
        ? value.trim()
        : '';

    const normalizedCurrent = normalizeOutgoingHtml(
      editor.getHTML(),
      editor.isEmpty
    );

    if (normalizedCurrent === normalizedIncoming) {
      return;
    }

    isSyncingFromValueRef.current = true;
    editor.commands.setContent(
      normalizedIncoming || '<p></p>'
    );
    isSyncingFromValueRef.current = false;
  }, [editor, value]);

  useEffect(() => {
    if (!editor) {
      return;
    }

    editor.setEditable(isInteractive);
  }, [editor, isInteractive]);

  const runEditorCommand = (
    command: () => boolean
  ) => {
    if (!editor || !isInteractive) {
      return;
    }

    command();
  };

  const isButtonDisabled = !editor || !isInteractive;

  const headingLevels = [1, 2, 3] as const;

  const toolbarButtons = [
    {
      key: 'paragraph',
      label: 'P',
      onClick: () =>
        runEditorCommand(() =>
          editor!.chain().focus().setParagraph().run()
        ),
      active: editor?.isActive('paragraph')
    },
    ...headingLevels.map((level) => ({
      key: `h${level}`,
      label: `H${level}`,
      onClick: () =>
        runEditorCommand(() =>
          editor!
            .chain()
            .focus()
            .toggleHeading({ level })
            .run()
        ),
      active: editor?.isActive('heading', {
        level
      })
    })),
    {
      key: 'bold',
      label: 'B',
      onClick: () =>
        runEditorCommand(() =>
          editor!.chain().focus().toggleBold().run()
        ),
      active: editor?.isActive('bold')
    },
    {
      key: 'italic',
      label: 'I',
      onClick: () =>
        runEditorCommand(() =>
          editor!.chain().focus().toggleItalic().run()
        ),
      active: editor?.isActive('italic')
    },
    {
      key: 'strike',
      label: 'S',
      onClick: () =>
        runEditorCommand(() =>
          editor!.chain().focus().toggleStrike().run()
        ),
      active: editor?.isActive('strike')
    },
    {
      key: 'bulletList',
      label: 'Bullets',
      onClick: () =>
        runEditorCommand(() =>
          editor!
            .chain()
            .focus()
            .toggleBulletList()
            .run()
        ),
      active: editor?.isActive('bulletList')
    },
    {
      key: 'orderedList',
      label: 'Numbered',
      onClick: () =>
        runEditorCommand(() =>
          editor!
            .chain()
            .focus()
            .toggleOrderedList()
            .run()
        ),
      active: editor?.isActive('orderedList')
    },
    {
      key: 'blockquote',
      label: 'Quote',
      onClick: () =>
        runEditorCommand(() =>
          editor!
            .chain()
            .focus()
            .toggleBlockquote()
            .run()
        ),
      active: editor?.isActive('blockquote')
    },
    {
      key: 'codeBlock',
      label: 'Code',
      onClick: () =>
        runEditorCommand(() =>
          editor!
            .chain()
            .focus()
            .toggleCodeBlock()
            .run()
        ),
      active: editor?.isActive('codeBlock')
    },
    {
      key: 'horizontalRule',
      label: 'HR',
      onClick: () =>
        runEditorCommand(() =>
          editor!
            .chain()
            .focus()
            .setHorizontalRule()
            .run()
        ),
      active: false
    },
    {
      key: 'undo',
      label: 'Undo',
      onClick: () =>
        runEditorCommand(() =>
          editor!.chain().focus().undo().run()
        ),
      active: false
    },
    {
      key: 'redo',
      label: 'Redo',
      onClick: () =>
        runEditorCommand(() =>
          editor!.chain().focus().redo().run()
        ),
      active: false
    }
  ];

  return (
    <div className="rich-editor-foundation">
      <p className="rich-editor-foundation__note">
        Editor mode: {mode}
      </p>

      <div
        className="rich-editor-foundation__toolbar"
        aria-label="Rich text formatting controls"
      >
        {toolbarButtons.map((button) => (
          <button
            key={button.key}
            type="button"
            onClick={button.onClick}
            disabled={isButtonDisabled}
            aria-pressed={Boolean(button.active)}
          >
            {button.label}
          </button>
        ))}
      </div>

      <div
        className="rich-editor-foundation__content"
        aria-label="Rich text editor"
      >
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};

export default ResourceRichTextEditor;
